import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Search, Users, Shield, KeyRound, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { usersAPI, rolesAPI, warehousesAPI, storesAPI } from '@/services/api';

const initialFormData = {
  username: '',
  password: '',
  employee_id: '',
  full_name: '',
  email: '',
  phone: '',
  role_id: '',
  entity_type: 'hq',
  entity_id: ''
};

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const fetchUsers = useCallback(async () => {
    try {
      const response = await usersAPI.list({ page, page_size: 20, search });
      setUsers(response.data.items);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch users',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [page, search, toast]);

  const fetchRolesAndEntities = async () => {
    try {
      const [rolesRes, warehousesRes, storesRes] = await Promise.all([
        rolesAPI.list(),
        warehousesAPI.list({ is_active: true }),
        storesAPI.list({ is_active: true })
      ]);
      setRoles(rolesRes.data);
      setWarehouses(warehousesRes.data);
      setStores(storesRes.data);
    } catch (error) {
      console.error('Failed to fetch roles/entities:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRolesAndEntities();
  }, [fetchUsers]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        username: user.username,
        password: '',
        employee_id: user.employee_id || '',
        full_name: user.full_name,
        email: user.email || '',
        phone: user.phone,
        role_id: user.role_id,
        entity_type: user.entity_type,
        entity_id: user.entity_id || ''
      });
    } else {
      setSelectedUser(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.username || !formData.full_name || !formData.phone || !formData.role_id) {
      toast({
        title: 'Validation Error',
        description: 'Username, Full Name, Phone, and Role are required',
        variant: 'destructive'
      });
      return;
    }

    if (!selectedUser && !formData.password) {
      toast({
        title: 'Validation Error',
        description: 'Password is required for new users',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      // Clean payload - convert empty strings to null for optional fields
      const payload = { ...formData };
      if (payload.email === '') payload.email = null;
      if (payload.employee_id === '') payload.employee_id = null;
      if (payload.entity_id === '') payload.entity_id = null;
      
      if (selectedUser) {
        delete payload.password;
        delete payload.username;
        await usersAPI.update(selectedUser.id, payload);
        toast({ title: 'Success', description: 'User updated successfully' });
      } else {
        await usersAPI.create(payload);
        toast({ title: 'Success', description: 'User created successfully' });
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      let errorMessage = 'Failed to save user';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMessage = detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      }
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await usersAPI.delete(selectedUser.id);
      toast({ title: 'Success', description: 'User deactivated successfully' });
      setDeleteDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete user',
        variant: 'destructive'
      });
    }
  };

  const handleResetPassword = async () => {
    try {
      const response = await usersAPI.resetPassword(selectedUser.id);
      toast({
        title: 'Password Reset',
        description: response.data.message
      });
      setResetDialogOpen(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to reset password',
        variant: 'destructive'
      });
    }
  };

  const filteredRoles = formData.entity_type
    ? roles.filter(r => String(r.entity_type || '').toLowerCase() === String(formData.entity_type).toLowerCase())
    : roles;

  const getEntityName = (entityType, entityId) => {
    if (!entityId) return '-';
    if (entityType === 'warehouse') {
      const warehouse = warehouses.find(w => w.id === entityId);
      return warehouse?.name || 'Unknown';
    }
    if (entityType === 'store') {
      const store = stores.find(s => s.id === entityId);
      return store?.name || 'Unknown';
    }
    return '-';
  };

  return (
    <div className="p-6 lg:p-8">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Users</h1>
          <p className="text-gray-500">Manage system users and access</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="add-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white border-gray-200 text-gray-800"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Users Table */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No users found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-transparent">
                  <TableHead className="text-gray-500">User</TableHead>
                  <TableHead className="text-gray-500">Role</TableHead>
                  <TableHead className="text-gray-500">Entity</TableHead>
                  <TableHead className="text-gray-500">Contact</TableHead>
                  <TableHead className="text-gray-500">Status</TableHead>
                  <TableHead className="text-gray-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(user => (
                  <TableRow
                    key={user.id}
                    className="border-gray-200 hover:bg-white"
                    data-testid={`user-row-${user.username}`}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium text-gray-800">{user.full_name}</p>
                        <p className="text-sm text-gray-500">@{user.username}</p>
                        {user.employee_id && (
                          <p className="text-xs text-gray-500">{user.employee_id}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-purple-500" />
                        <span className="text-gray-600">{user.role_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <Badge variant="outline" className="border-gray-300 text-gray-600 capitalize">
                          {user.entity_type}
                        </Badge>
                        {user.entity_name && (
                          <p className="text-xs text-gray-500 mt-1">{user.entity_name}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="flex items-center gap-1 text-gray-600">
                          <Phone className="w-3 h-3" />
                          {user.phone}
                        </div>
                        {user.email && (
                          <div className="flex items-center gap-1 text-gray-500">
                            <Mail className="w-3 h-3" />
                            {user.email}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.is_active ? 'default' : 'secondary'}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                          onClick={() => {
                            setSelectedUser(user);
                            setResetDialogOpen(true);
                          }}
                          title="Reset Password"
                        >
                          <KeyRound className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                          onClick={() => handleOpenDialog(user)}
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => {
                            setSelectedUser(user);
                            setDeleteDialogOpen(true);
                          }}
                          title="Deactivate"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="border-gray-300 text-gray-600"
          >
            Previous
          </Button>
          <span className="px-4 py-2 text-gray-500">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="border-gray-300 text-gray-600"
          >
            Next
          </Button>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedUser ? 'Edit User' : 'Add User'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-600">Username *</Label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData(p => ({ ...p, username: e.target.value }))}
                  placeholder="Username"
                  className="bg-gray-50 border-gray-300"
                  disabled={!!selectedUser}
                  data-testid="username-input"
                />
              </div>
              {!selectedUser && (
                <div className="space-y-2">
                  <Label className="text-gray-600">Password *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))}
                    placeholder="Password"
                    className="bg-gray-50 border-gray-300"
                    data-testid="password-input"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-600">Full Name *</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData(p => ({ ...p, full_name: e.target.value }))}
                  placeholder="Full Name"
                  className="bg-gray-50 border-gray-300"
                  data-testid="fullname-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-600">Employee ID</Label>
                <Input
                  value={formData.employee_id}
                  onChange={(e) => setFormData(p => ({ ...p, employee_id: e.target.value }))}
                  placeholder="EMP001"
                  className="bg-gray-50 border-gray-300"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-600">Phone *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                  placeholder="Phone number"
                  className="bg-gray-50 border-gray-300"
                  data-testid="phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-600">Email</Label>
                <Input
                  value={formData.email}
                  onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                  placeholder="Email address"
                  className="bg-gray-50 border-gray-300"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-600">Entity Type *</Label>
              <Select
                value={formData.entity_type}
                onValueChange={(value) => setFormData(p => ({ ...p, entity_type: value, role_id: '', entity_id: '' }))}
              >
                <SelectTrigger className="bg-gray-50 border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="hq">Headquarters (HQ)</SelectItem>
                  <SelectItem value="warehouse">Warehouse</SelectItem>
                  <SelectItem value="store">Store</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.entity_type !== 'hq' && (
              <div className="space-y-2">
                <Label className="text-gray-600">
                  {formData.entity_type === 'warehouse' ? 'Warehouse' : 'Store'} *
                </Label>
                <Select
                  value={formData.entity_id}
                  onValueChange={(value) => setFormData(p => ({ ...p, entity_id: value }))}
                >
                  <SelectTrigger className="bg-gray-50 border-gray-300">
                    <SelectValue placeholder={`Select ${formData.entity_type}`} />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {(formData.entity_type === 'warehouse' ? warehouses : stores).map(entity => (
                      <SelectItem key={entity.id} value={entity.id}>
                        {entity.name} ({entity.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-gray-600">Role *</Label>
              <Select
                value={formData.role_id}
                onValueChange={(value) => setFormData(p => ({ ...p, role_id: value }))}
              >
                <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {filteredRoles.map(role => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-gray-300 text-gray-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="save-user-btn"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Deactivate User?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This will deactivate the user "{selectedUser?.full_name}". They will no longer be able to log in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300 text-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Confirmation */}
      <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Reset Password?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This will reset the password for "{selectedUser?.full_name}" to their default password (username@123).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300 text-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetPassword}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              Reset Password
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersPage;
